import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as lookupSlice from '../../../data/lookup.slice';
import * as clientSlice from '../../../data/client.slice';
import * as parentOrganizationsSlice from '../../../data/parent-organizations.slice';
import { useTranslation } from 'react-i18next';
import { Client } from '../../../data/models/client.model';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useOnSaved } from '../../../hooks/useOnSaved';
import SelectMenu from '../../forms/SelectMenu';

const PREFIX = 'ParentOrganizationDialog';

const classes = {
  content: `${PREFIX}-content`,
  select: `${PREFIX}-select`,
  actions: `${PREFIX}-actions`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    display: 'flex',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.select}`]: {
    flexGrow: 1,
    marginTop: theme.spacing(1),
  },

  [`& .${classes.actions}`]: {
    gap: theme.spacing(1),
  },
}));

interface ParentOrganizationDialogProps {
  open: boolean;
  client?: Client;
  close: () => void;
}

export default function ParentOrganizationDialog({
  open,
  client,
  close,
}: ParentOrganizationDialogProps) {
  const { t } = useTranslation('common');
  const requiredFieldMessage = t('common.requiredField');
  const dispatch = useAppDispatch();

  const savedClient = useAppSelector(clientSlice.selectSavedClient);
  const parentOrganizations = useAppSelector(
    parentOrganizationsSlice.selectParentOrganizations,
  );
  const savingClient = useAppSelector(clientSlice.selectSavingClient);

  const formHook = useForm({
    mode: 'onTouched',
    defaultValues: { ParentOrganization: client?.parentOrganizationBC || '' },
  });

  React.useEffect(() => {
    dispatch(parentOrganizationsSlice.fetchParentOrganizationsAsync());
    dispatch(lookupSlice.fetchLookupItemsAsync('clientVertical'));
  }, []);

  const handleClose = () => {
    formHook.reset();
    close();
  };

  useOnSaved(savedClient, handleClose);

  const assignParentOrganization = (
    client: Client,
    parentOrganizationBC: string,
  ) => {
    if (
      parentOrganizationBC &&
      client.parentOrganizationBC !== parentOrganizationBC
    ) {
      dispatch(
        clientSlice.assignClientToParentOrganizationAsync(
          client.clientBC,
          parentOrganizationBC,
        ),
      );
    } else {
      handleClose();
    }
  };

  const handleSave = (data: any) => {
    assignParentOrganization(client!, data.ParentOrganization);
  };

  return (
    <RootDialog open={open} onClose={handleClose}>
      <DialogTitle>
        {t('client.editParentOrganization')}
        {savingClient && (
          <LinearProgress style={{ width: '100%' }} color="primary" />
        )}
      </DialogTitle>
      <DialogContent className={classes.content}>
        <SelectMenu
          label={t('client.parentOrganization')}
          name="ParentOrganization"
          values={parentOrganizations}
          valueAttr="parentOrganizationBC"
          labelAttr="parentOrganizationName"
          className={classes.select}
          required={true}
          control={formHook.control}
          rules={{ required: requiredFieldMessage }}
        />
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button variant="contained" color="secondary" onClick={handleClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!formHook.formState.isValid || savingClient}
          onClick={formHook.handleSubmit(handleSave)}>
          {t('common.ok')}
        </Button>
      </DialogActions>
    </RootDialog>
  );
}
